import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './MessagesByTopic.module.scss';
import { useFetch } from 'hooks/useFetch';
import { dashboardEndpoints } from 'hooks/endpoints';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import Select from 'component/atom/forms/select/select/Select';
import Modal from 'component/hoc/modal/Modal';
import TableComponent from 'component/molecules/Table/TableComponent';

import Loader from 'component/atom/loader/Loader';
import NoData from 'component/molecules/Table/TableComponents/NoData/NoData';

const MESSAGES_COLORS = ['#1859F1', '#99D315', '#FFA61A', '#7B35D8', '#18C0C7'];
const MIN_HEIGHT = 370;

const MessagesByTopic = ({ id, startDate, endDate, snapshotMode, botType }) => {
  const MAX_HEIGHT = snapshotMode ? 600 : 370;
  const chartRef = useRef(null);
  const [series, setSeries] = useState([{ name: 'Messages', data: [] }]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategory, setSelectedCategory] = useState({});

  const ROW_SLOT = 56;

  const rowCount = useMemo(() => categories?.length || 0, [categories]);

  const chartHeight = useMemo(() => {
    const h = rowCount * ROW_SLOT;
    return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, h));
  }, [rowCount, MAX_HEIGHT]);

  const barHeightPct = useMemo(
    () => (rowCount <= 1 ? '32%' : snapshotMode ? '90%' : '60%'),
    [rowCount, snapshotMode]
  );

  const { data, refetch } = useFetch(
    ['chat-categories', startDate, endDate, id, botType],
    dashboardEndpoints.GetChatCategoriesByTopic(
      startDate,
      endDate,
      id,
      botType,
      selectedCountry
    ),
    { enabled: false }
  );

  const { data: countries, refetch: refetchCountries } = useFetch(
    ['countries', startDate, endDate, id, botType],
    dashboardEndpoints.getMesssagesByTopicCountries(startDate, endDate),
    { enabled: false },
    id ? { botId: id, chatSource: botType } : {}
  );

  useEffect(() => {
    setSelectedCountry('');
  }, [id]);

  const {
    data: questionsByCategory,
    refetch: refetchQuestionsByCategory,
    isLoading,
    isFetching,
  } = useFetch(
    [
      'questions-by-category',
      startDate,
      endDate,
      id,
      selectedCategory?.categoryId,
      botType,
      selectedCountry,
    ],
    dashboardEndpoints.getQuesByTopic(startDate, endDate),
    { enabled: false },
    {
      ...(id && { botId: id, chatSource: botType }),
      ...(selectedCategory?.topicId && {
        topicId: selectedCategory.topicId,
      }),
      ...(selectedCountry && { country: selectedCountry }),
    }
  );

  useEffect(() => {
    refetchQuestionsByCategory();
  }, [selectedCategory, id]);

  useEffect(() => {
    if (botType !== undefined) {
      refetch();
      refetchCountries();
    }
  }, [startDate, endDate, selectedCountry, botType, id]);

  useEffect(() => {
    if (showModal) {
      refetchQuestionsByCategory();
    }
  }, [showModal]);

  useEffect(() => {
    const rows = data?.data || [];
    const cats = rows.map((r) => r.topicText);
    const vals = rows.map((r) => r.chatCount ?? 0);
    setCategories(cats);
    setSeries([{ name: 'Messages', data: vals }]);
  }, [data]);

  const valueAnnotations = useMemo(() => {
    if (!series[0]?.data?.length) return [];

    return series[0].data.map((val, i) => ({
      x: val,
      y: categories[i],
      marker: {
        size: 0,
        strokeWidth: 0,
        fillColor: 'transparent',
        strokeColor: 'transparent',
      },
      label: {
        text: String(val),
        style: {
          background: 'transparent',
          color: '#000',
          fontSize: '14px',
          fontWeight: 600,
        },
        borderColor: 'transparent',
        borderWidth: 0,
        offsetX: 12,
        offsetY: rowCount === 1 ? 0 : 8,
      },
    }));
  }, [series, categories, rowCount]);

  const maxVal = useMemo(() => {
    const arr = series?.[0]?.data || [];
    return arr?.length ? Math.max(...arr) : 0;
  }, [series]);

  const chartSettings = useMemo(
    () => ({
      series,
      options: {
        chart: {
          id: 'messages-by-topic-chart',
          type: 'bar',
          height: chartHeight,
          background: '#FFFFFF',
          toolbar: { show: false },
          animations: { enabled: true },
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              const idx = config?.dataPointIndex ?? -1;
              const rows = data?.data || [];
              const selected = rows[idx];
              if (selected) {
                setSelectedCategory(selected);
                setShowModal(true);
              }
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: barHeightPct,
            borderRadius: 8,
            borderRadiusApplication: 'end',
            borderRadiusWhenStacked: 'last',
            endingShape: 'rounded',
            distributed: true,
            dataLabels: { position: 'bottom' },
          },
        },
        colors: MESSAGES_COLORS,
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories,
          labels: { show: true },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tickAmount: 5,
        },
        yaxis: {
          show: true,
          labels: {
            show: true,
            maxWidth: 140,
            style: { fontSize: '12px' },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        grid: {
          show: true,
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { top: 8, right: 80, bottom: 8, left: 20 },
        },
        tooltip: { enabled: true },
        legend: { show: false },
        annotations: {
          position: 'front',
          points: valueAnnotations,
        },
      },
    }),
    [series, categories, valueAnnotations, chartHeight, data, maxVal]
  );

  const toCsv = (rows, headers) => {
    const escapeCell = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      if (
        s.indexOf('"') !== -1 ||
        s.indexOf(',') !== -1 ||
        s.indexOf('\n') !== -1
      ) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const headerLine = headers.map((h) => escapeCell(h.headerName)).join(',');
    const lines = rows.map((r) =>
      headers.map((h) => escapeCell(r[h.fieldName])).join(',')
    );
    return [headerLine].concat(lines).join('\n');
  };

  const handleDownload = () => {
    const rows = (data?.data || []).map((r) => ({
      topic: r.topicText,
      messages: r.chatCount ?? 0,
    }));
    if (!rows?.length) return;

    const headers = [
      { fieldName: 'topic', headerName: 'Topic' },
      { fieldName: 'messages', headerName: 'Messages' },
    ];

    const csv = '\ufeff' + toCsv(rows, headers);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'messages_by_topic.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div ref={chartRef} className={styles['chart-container']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <span
            className={`${styles['title']} ${
              snapshotMode && styles['snapshotMode-title']
            }`}
          >
            Breakdown of Messages by Topic
          </span>
          {!snapshotMode && (
            <Tooltip
              content='Displays the number of user messages about a 
selected topic ( e.g., admissions), filterable by country'
              className={styles['tooltip']}
            >
              <SvgWithBranding type='info' color='#682ECB' width={22} />
            </Tooltip>
          )}
        </div>
        {!snapshotMode && (
          <div className={styles['actions']}>
            <Select
              placeholder='All Countries'
              selectClassname={styles['select']}
              classname={styles['select-container']}
              inputClassName={styles['select-input']}
              value={selectedCountry || 'ALL'}
              onChange={(e) => setSelectedCountry(e.target.value)}
              options={[
                { value: '', label: 'All Countries' },
                ...(countries?.data || []).map((c) => ({
                  value: c,
                  label: c,
                })),
              ]}
            />
            {series[0]?.data?.length === 0 ? (
              <Tooltip content='No data available to export'>
                <SvgWithBranding
                  className={`${styles['download-icon']} ${styles['disabled']}`}
                  type='download2'
                  onClick={() => {}}
                />
              </Tooltip>
            ) : (
              <SvgWithBranding
                className={`${styles['download-icon']}`}
                type='download2'
                onClick={handleDownload}
              />
            )}
          </div>
        )}
      </div>
      {snapshotMode && (
        <p className='snap-desc'>
          Displays the number of user messages per topic
        </p>
      )}
      {isLoading || isFetching ? (
        <Loader style={{ marginTop: '80px' }} />
      ) : (chartSettings?.series?.[0]?.data?.length ?? 0) === 0 ? (
        <NoData containerStyle={{ minHeight: '70%' ?? 370 }} />
      ) : (
        <ReactApexChart
          options={chartSettings.options}
          series={chartSettings.series}
          type='bar'
          height={chartHeight}
        />
      )}

      <Modal
        title={selectedCategory?.topicText}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contentClassName={styles['modal']}
        classname={styles['modal-container']}
        children={
          isLoading || isFetching ? (
            <Loader />
          ) : (
            <TableComponent
              rowKey='text'
              tableContainerClassName={styles['table-container']}
              headerData={{
                tableName: 'Application Status',
                hasExport: false,
                hasSearch: false,
                hasDelete: false,
                hasEdit: false,
                hasPaging: true,
                hasCheckbox: false,
                hasUploadCSV: false,
                hasFilter: false,
                hasExport: false,
                isExpandable: true,
                fields: [
                  { fieldName: 'text', headerName: 'Question' },
                  {
                    fieldName: 'country',
                    headerName: 'Country',
                    hasSort: false,
                  },
                  {
                    fieldName: 'dateCreated',
                    headerName: 'Date',
                    isDatetime: true,
                  },
                ],
              }}
              externalData={questionsByCategory?.data || []}
            />
          )
        }
      />
    </div>
  );
};

export default MessagesByTopic;
